import { memo, useCallback, useState } from "react";
import { IoPersonSharp, IoPeopleSharp } from "react-icons/io5";
import { EmptyState, Loader, Modal as ConfirmModal } from "../../components";
import { ScorecardServiceProvider } from "./contexts/ScorecardServiceContext";
import useScorecardData from "./hooks/useScorecardData";
import {
  ScorecardTopbar,
  ScorecardHistory,
  ScorecardFormGrid,
  GoalDialog,
  PreviousMonthDialog,
  FeedbackDialog,
  StaffSelector,
} from "./components";
import "./index.css";

function HomePageContent() {
  const {
    draft,
    activeDraft,
    isLoading,
    isHistoryLoading,
    isSaving,
    isDataRefreshing,
    isGoalSaving,
    isGoalDeleting,
    isReflectionSaving,
    activeLoadError,
    historyError,
    lastHydratedAt,
    scorecardHistory,
    goalDialog,
    previousMonthDialog,
    historySelection,
    discardDialogOpen,
    isViewingHistory,
    isPanelLoading,
    currentPeriodKpiDataIds,
    groups,
    totalFields,
    missingFields,
    bandTotals,
    budgetTotals,
    summaryTone,
    previousMonthGoals,
    hasUnsavedChanges,
    findGoal,
    loadCurrentScorecard,
    refreshCurrentScorecard,
    triggerDataRefresh,
    handleHistorySelection,
    handleStaffSelection,
    switchManagerView,
    updateItemValue,
    saveScorecard,
    openGoalDialog,
    closeGoalDialog,
    updateGoalFormField,
    saveGoal,
    deleteGoal,
    openPreviousMonthDialog,
    closePreviousMonthDialog,
    updatePreviousMonthReflectionNotes,
    savePreviousMonthReflection,
    closeCurrentScorecard,
    closeDiscardDialog,
    cancelActiveView,
    isAdmin,
    isManager,
    managerViewMode,
    subordinates,
    isSubordinatesLoading,
    selectedStaffEmail,
    currentUserEmail,
    feedbackDialogOpen,
    isFeedbackSending,
    isFeedbackDeleting,
    openFeedbackDialog,
    closeFeedbackDialog,
    submitFeedback,
    deleteFeedback,
  } = useScorecardData();

  const handleTabSwitch = useCallback((tab) => {
    if (tab !== managerViewMode) {
      switchManagerView(tab);
    }
  }, [managerViewMode, switchManagerView]);

  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);
  const openRefreshConfirm = useCallback(() => setRefreshConfirmOpen(true), []);
  const closeRefreshConfirm = useCallback(() => setRefreshConfirmOpen(false), []);
  const confirmRefresh = useCallback(() => {
    setRefreshConfirmOpen(false);
    refreshCurrentScorecard();
  }, [refreshCurrentScorecard]);

  const [dataRefreshConfirmOpen, setDataRefreshConfirmOpen] = useState(false);
  const openDataRefreshConfirm = useCallback(() => setDataRefreshConfirmOpen(true), []);
  const closeDataRefreshConfirm = useCallback(() => setDataRefreshConfirmOpen(false), []);
  const confirmDataRefresh = useCallback(() => {
    setDataRefreshConfirmOpen(false);
    triggerDataRefresh();
  }, [triggerDataRefresh]);

  const isMyTeamTab = managerViewMode === "my-team";
  const isMyScorecardTab = managerViewMode === "my-scorecard";
  const tabsDisabled = Boolean(activeDraft) || isPanelLoading;

  const selectedStaffName = subordinates.find(
    (staff) => staff.employeeEmail === selectedStaffEmail
  )?.employeeName ?? "";

  return (
    <div className="home-page">
      <ScorecardTopbar
        summaryTone={summaryTone}
        isLoading={isLoading}
        isSaving={isSaving}
        isDataRefreshing={isDataRefreshing}
        isGoalSaving={isGoalSaving}
        isViewingHistory={isViewingHistory}
        draft={activeDraft}
        lastHydratedAt={lastHydratedAt}
        totalFields={totalFields}
        missingFields={missingFields}
        bandTotals={bandTotals}
        budgetTotals={budgetTotals}
        scorecardHistory={scorecardHistory}
        historyLoading={historySelection.loading}
        previousMonthLoading={previousMonthDialog.loading}
        onLoadCurrent={loadCurrentScorecard}
        onRefresh={openRefreshConfirm}
        onTriggerDataRefresh={openDataRefreshConfirm}
        onSave={saveScorecard}
        onCancel={cancelActiveView}
        isAdmin={isAdmin}
        isManager={isManager}
        managerViewMode={managerViewMode}
        hasUnsavedChanges={hasUnsavedChanges}
        onOpenFeedback={openFeedbackDialog}
      />

      <div className="home-content">
        {isManager && (
          <nav className="home-tab-bar" role="tablist" aria-label="Scorecard view">
            <button
              role="tab"
              aria-selected={isMyTeamTab}
              className={`home-tab${isMyTeamTab ? " active" : ""}`}
              onClick={() => handleTabSwitch("my-team")}
              disabled={tabsDisabled}
            >
              <IoPeopleSharp /> My Team
            </button>
            <button
              role="tab"
              aria-selected={isMyScorecardTab}
              className={`home-tab${isMyScorecardTab ? " active" : ""}`}
              onClick={() => handleTabSwitch("my-scorecard")}
              disabled={tabsDisabled}
            >
              <IoPersonSharp /> My Scorecards
            </button>
          </nav>
        )}

        <section className="home-app-list-panel" aria-label="Scorecard editor" role="tabpanel">
          {!activeDraft && !isPanelLoading && isManager && isMyTeamTab && (
            <StaffSelector
              subordinates={subordinates}
              selectedEmail={selectedStaffEmail}
              isLoading={isSubordinatesLoading}
              onSelect={handleStaffSelection}
            />
          )}

          {!activeDraft && !isPanelLoading && (
            (!isManager || (isMyTeamTab && selectedStaffEmail) || isMyScorecardTab)
          ) && (
            <ScorecardHistory
              scorecardHistory={scorecardHistory}
              historyError={historyError}
              isHistoryLoading={isHistoryLoading}
              historySelection={historySelection}
              isViewingHistory={isViewingHistory}
              currentPeriodKpiDataIds={currentPeriodKpiDataIds}
              selectedUserName={selectedStaffName}
              onSelect={handleHistorySelection}
            />
          )}

          {isPanelLoading && <Loader content={isViewingHistory ? "Loading historical scorecard" : "Loading current scorecard"} />}

          {!isPanelLoading && activeLoadError && (
            <EmptyState
              title={isViewingHistory ? "Historical scorecard unavailable" : "Current scorecard unavailable"}
              message={activeLoadError}
              className="home-empty-state"
            />
          )}

          {!isPanelLoading && !activeLoadError && activeDraft && (
            <ScorecardFormGrid
              groups={groups}
              isViewingHistory={isViewingHistory}
              hasPreviousMonth={Boolean(activeDraft?.previousMonthKPIDataId)}
              currentGoals={activeDraft?.goals ?? []}
              previousMonthGoals={previousMonthGoals}
              findGoal={findGoal}
              onUpdateItemValue={updateItemValue}
              onOpenGoalDialog={openGoalDialog}
              onOpenPreviousMonthDialog={openPreviousMonthDialog}
            />
          )}
        </section>
      </div>

      <ConfirmModal
        title="Discard unsaved changes?"
        text="Any unsaved scorecard changes will be lost if you continue."
        confirmButtonText="Discard"
        cancelButtonText="Keep editing"
        confirmColor="red"
        cancelColor="blue"
        onConfirm={closeCurrentScorecard}
        onClose={closeDiscardDialog}
        open={discardDialogOpen}
      />

      <ConfirmModal
        title="Refresh KPI data?"
        text="This re-pulls the latest KPI data from the API and overwrites any unsaved changes on this scorecard."
        confirmButtonText="Refresh"
        cancelButtonText="Cancel"
        confirmColor="blue"
        cancelColor="red"
        onConfirm={confirmRefresh}
        onClose={closeRefreshConfirm}
        open={refreshConfirmOpen}
      />

      <ConfirmModal
        title="Refresh data?"
        text="This triggers a refresh of the source data."
        confirmText="CONFIRM"
        confirmButtonText="Refresh Data"
        cancelButtonText="Cancel"
        confirmColor="blue"
        cancelColor="red"
        loading={isDataRefreshing}
        onConfirm={confirmDataRefresh}
        onClose={closeDataRefreshConfirm}
        open={dataRefreshConfirmOpen}
      />

      <GoalDialog
        goalDialog={goalDialog}
        isGoalSaving={isGoalSaving}
        isGoalDeleting={isGoalDeleting}
        onClose={closeGoalDialog}
        onUpdateField={updateGoalFormField}
        onSave={saveGoal}
        onDelete={deleteGoal}
      />

      <PreviousMonthDialog
        previousMonthDialog={previousMonthDialog}
        isReflectionSaving={isReflectionSaving}
        onClose={closePreviousMonthDialog}
        onUpdateReflectionNotes={updatePreviousMonthReflectionNotes}
        onSaveReflection={savePreviousMonthReflection}
      />

      <FeedbackDialog
        open={feedbackDialogOpen}
        feedbacks={activeDraft?.feedbacks ?? []}
        kpiDataId={activeDraft?.kpiDataId}
        currentUserEmail={currentUserEmail}
        isManager={isManager}
        isSending={isFeedbackSending}
        isDeleting={isFeedbackDeleting}
        onClose={closeFeedbackDialog}
        onSubmit={submitFeedback}
        onDelete={deleteFeedback}
      />
    </div>
  );
}

function HomePage() {
  return (
    <ScorecardServiceProvider>
      <HomePageContent />
    </ScorecardServiceProvider>
  );
}

export default memo(HomePage);
